import React, { useState, useEffect } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../../firebase";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { useCase } from "../../../../contexts/CaseContext";
import { useAccount } from "../../../../contexts/AccountContext";
import { Input } from "../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../../design/fieldset";
import { Textarea } from "../../../../design/textarea";
import { Switch, SwitchField } from "../../../../design/switch";
import { Select } from "../../../../design/select";
import { FileCategory, FILE_CATEGORY_LABELS, FILE_CATEGORY_ORDER, storagePaths } from "@visitproof/types";

interface UploadFileButtonProps {
  caseId: string;
}

const UploadCaseFileButton: React.FC<UploadFileButtonProps> = ({ caseId }) => {
  const { addFileToCase } = useCase();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [category, setCategory] = useState<FileCategory>("misc");
  const [isVisibleToSupervisor, setIsVisibleToSupervisor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (file && !name) {
      setName(file.name);
    }
  }, [file, name]);

  const handleSubmit = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);

    try {
      const path = account?.id
        ? storagePaths.caseFile(account.id, caseId, file.name)
        : `cases/${caseId}/${file.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      await addFileToCase(caseId, {
        path: fileRef.fullPath,
        name,
        description,
        category,
        isVisibleToSupervisor,
      });
      clearForm();
    } catch (error) {
      console.error("File upload failed:", error);
      const message = error instanceof Error && error.message.includes("permission")
        ? "You don't have permission to upload files to this case."
        : "Failed to upload file. Please try again later.";
      alert(message);
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setCategory("misc");
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create File
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) =>
                    setFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  placeholder="Name"
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Category</Label>
                <Description>Organize files by type for easier access</Description>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FileCategory)}
                >
                  {FILE_CATEGORY_ORDER.map((cat) => (
                    <option key={cat} value={cat}>
                      {FILE_CATEGORY_LABELS[cat]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  placeholder="Description"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <SwitchField>
                <Label>Visible to Monitor</Label>
                <Switch
                  checked={isVisibleToSupervisor}
                  onChange={() =>
                    setIsVisibleToSupervisor(!isVisibleToSupervisor)
                  }
                />
              </SwitchField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={!file || isUploading} onClick={handleSubmit}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadCaseFileButton;
